import { Box, Paper, Typography, useTheme } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import PlaceIcon from "@mui/icons-material/Place";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import SensorsIcon from "@mui/icons-material/Sensors";
import { unix_utc_to_local_12_hour_clock } from "../../../../utils/date_time_utils";
import { memo, useMemo } from "react";

/**
 * Recent Events Widget
 * Shows the most recent asset/inventory events
 * Memoized to prevent unnecessary re-renders
 */
const RecentEvents = memo(function RecentEvents({ assets = [], facilities = {}, timeZone }) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, warning = {}, background = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";

  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const bgPaper = background?.paper || (isDark ? "#032d42" : "#ffffff");
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0");

  // Memoize recent events calculation
  const recentEvents = useMemo(() => {
    return assets
      .filter(asset => asset.timeOfLogLong)
      .sort((a, b) => (b.timeOfLogLong || 0) - (a.timeOfLogLong || 0))
      .slice(0, 8)
      .map(asset => {
        const facilityName = asset.facilityId && facilities[asset.facilityId]
          ? facilities[asset.facilityId].name
          : null;
        
        return {
          id: asset.assetId,
          tag: asset.tag || "Unknown",
          event: asset.lastEvent || "Event",
          time: asset.timeOfLogLong,
          facility: facilityName,
          hasLocation: !!(asset.latitude && asset.longitude),
        };
      });
  }, [assets, facilities]);

  // Get icon based on event type
  const getEventIcon = (event) => {
    const eventLower = (event || "").toLowerCase();
    if (eventLower.includes("moving") || eventLower.includes("transit") || eventLower.includes("ship")) {
      return <LocalShippingIcon sx={{ fontSize: "1rem" }} aria-hidden="true" />;
    }
    if (eventLower.includes("arrival") || eventLower.includes("warehouse") || eventLower.includes("receive")) {
      return <WarehouseIcon sx={{ fontSize: "1rem" }} aria-hidden="true" />;
    }
    if (eventLower.includes("scan") || eventLower.includes("proximity")) {
      return <SensorsIcon sx={{ fontSize: "1rem" }} aria-hidden="true" />;
    }
    return <PlaceIcon sx={{ fontSize: "1rem" }} aria-hidden="true" />;
  };

  // Get color based on event type
  const getEventColor = (event) => {
    const eventLower = (event || "").toLowerCase();
    if (eventLower.includes("moving") || eventLower.includes("transit")) {
      return "#3b82f6";
    }
    if (eventLower.includes("arrival") || eventLower.includes("receive")) {
      return greenColor;
    }
    if (eventLower.includes("alert") || eventLower.includes("warning")) {
      return warning?.main || "#f59e0b";
    }
    return isDark ? "#94a3b8" : "#64748b";
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return unix_utc_to_local_12_hour_clock(timestamp, timeZone);
  };

  // Format full time for screen readers
  const formatFullTime = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Paper
      component="article"
      role="region"
      aria-label="Recent Events"
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: "16px",
        border: `1px solid ${borderColor}`,
        background: isDark
          ? "linear-gradient(145deg, #032d42 0%, #021e2d 100%)"
          : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
        boxShadow: isDark ? "0 4px 20px rgba(0, 0, 0, 0.3)" : "0 4px 20px rgba(0, 0, 0, 0.06)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: isDark
            ? `linear-gradient(90deg, ${greenColor} 0%, ${navyColor} 100%)`
            : `linear-gradient(90deg, ${navyColor} 0%, ${greenColor} 100%)`,
        },
      }}
    >
      {/* Header – same pattern as map (icon 36 + title + subtitle) */}
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          background: "transparent",
          borderBottom: `1px solid ${borderColor}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: isDark
                ? `linear-gradient(135deg, ${navyColor}40 0%, ${navyColor}20 100%)`
                : `linear-gradient(135deg, ${navyColor}15 0%, ${navyColor}05 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${navyColor}30`,
            }}
          >
            <HistoryIcon sx={{ color: isDark ? greenColor : navyColor, fontSize: "1.2rem" }} />
          </Box>
          <Box>
            <Typography
              component="h2"
              sx={{
                fontWeight: 600,
                color: isDark ? greenColor : navyColor,
                fontSize: "1rem",
                lineHeight: 1.2,
              }}
            >
              Recent Events
            </Typography>
            <Typography
              sx={{
                color: textSecondary,
                fontSize: "0.75rem",
              }}
            >
              Latest activity
            </Typography>
          </Box>
        </Box>
        <Box
          role="status"
          aria-label={`${recentEvents.length} events`}
          sx={{
            padding: "4px 10px",
            borderRadius: "12px",
            background: isDark ? "rgba(160, 196, 109, 0.15)" : "rgba(160, 196, 109, 0.1)",
            border: `1px solid ${greenColor}30`,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              color: greenColor,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {recentEvents.length} Events
          </Typography>
        </Box>
      </Box>

      {/* Events List */}
      <Box
        component="ul"
        role="list"
        aria-label="Event list"
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "8px 0",
          margin: 0,
          listStyle: "none",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? "rgba(148, 163, 184, 0.3)" : "rgba(0, 0, 0, 0.15)",
            borderRadius: "3px",
          },
        }}
      >
        {recentEvents.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: textSecondary,
              fontSize: "0.875rem",
            }}
          >
            No recent events
          </Box>
        ) : (
          recentEvents.map((event, index) => {
            const eventColor = getEventColor(event.event);
            return (
              <Box
                component="li"
                key={event.id || index}
                tabIndex={0}
                aria-label={`${event.tag}: ${event.event}${event.facility ? ` at ${event.facility}` : ''}, ${formatFullTime(event.time)}`}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  padding: "10px 16px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  "&:hover, &:focus": {
                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
                    outline: "none",
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${greenColor}`,
                    outlineOffset: "-2px",
                  },
                }}
              >
                {/* Icon */}
                <Box
                  aria-hidden="true"
                  sx={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    backgroundColor: `${eventColor}15`,
                    border: `1px solid ${eventColor}30`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: eventColor,
                    flexShrink: 0,
                  }}
                >
                  {getEventIcon(event.event)}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        color: textPrimary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {event.tag}
                    </Typography>
                    <Typography
                      aria-hidden="true"
                      sx={{
                        fontSize: "0.7rem",
                        color: textSecondary,
                        flexShrink: 0,
                      }}
                    >
                      {formatTimeAgo(event.time)}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: eventColor,
                      fontWeight: 500,
                    }}
                  >
                    {event.event}
                  </Typography>
                  {event.facility && (
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: textSecondary,
                        marginTop: "2px",
                      }}
                    >
                      <span aria-hidden="true">📍</span> {event.facility}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
});

export default RecentEvents;
